import { useState, useEffect, useCallback } from 'react';
import { fetchRequests } from '../../../Requests/getRequests';
import { authStaff } from '../../../utils/constants';
import { useSelector } from 'react-redux';
import { selectAccessToken } from '../../../slices/staffSlice';
import { Topbar, Sidebar, Loader } from '../../../components';
import { HugeiconsIcon } from '@hugeicons/react';
import { CheckmarkCircle01Icon, Cancel01Icon, ArrowRight01Icon, ArrowDown01Icon } from '@hugeicons/core-free-icons';

const SERVICES = [
    { name: 'Auth Service',    key: 'auth',     path: '/auth/health' },
    { name: 'Loan Service',    key: 'loans',    path: '/loans/health' },
    { name: 'Payment Service', key: 'payments', path: '/payments/health' },
    { name: 'Report Service',  key: 'reports',  path: '/reports/health' },
];

const DAYS = 90;

function barColor(upPct) {
    if (upPct === null) return 'bg-gray-200 dark:bg-gray-700';
    if (upPct >= 95)   return 'bg-emerald-500';
    if (upPct >= 70)   return 'bg-amber-400';
    return 'bg-red-500';
}

function buildDateRange(days) {
    const arr = [];
    for (let i = days - 1; i >= 0; i--) {
        const d = new Date();
        d.setDate(d.getDate() - i);
        arr.push(d.toISOString().slice(0, 10));
    }
    return arr;
}

function timeAgo(dateStr) {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins  = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days  = Math.floor(diff / 86400000);
    if (mins < 1)    return 'just now';
    if (mins < 60)   return `${mins}m ago`;
    if (hours < 24)  return `${hours}h ago`;
    return `${days}d ago`;
}

function ErrorPanel({ errors, loading }) {
    const [expanded, setExpanded] = useState(null);
    const [open, setOpen] = useState(false);

    if (loading) {
        return (
            <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-700 flex items-center gap-2 text-xs text-gray-400">
                <Loader color="text-gray-400" size="w-3.5 h-3.5" /> Loading errors…
            </div>
        );
    }

    if (!errors || errors.length === 0) {
        return (
            <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-700 flex items-center gap-1.5 text-xs text-emerald-600 dark:text-emerald-400">
                <HugeiconsIcon icon={CheckmarkCircle01Icon} size={13} />
                No errors in the last 30 days
            </div>
        );
    }

    return (
        <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-700">
            <button
                type="button"
                onClick={() => setOpen(!open)}
                className="w-full flex items-center justify-between py-1 group"
            >
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400">
                    {errors.length} error{errors.length !== 1 ? 's' : ''} in last 30 days
                </p>
                <HugeiconsIcon
                    icon={ArrowDown01Icon}
                    size={14}
                    className={`text-gray-400 group-hover:text-gray-600 dark:group-hover:text-gray-300 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
                />
            </button>

            {open && (
                <div className="space-y-1.5 mt-2">
                    {errors.map((err, i) => (
                        <div key={i} className="rounded-lg border border-gray-100 dark:border-gray-700 overflow-hidden">
                            <button
                                type="button"
                                onClick={() => setExpanded(expanded === i ? null : i)}
                                className="w-full flex items-start justify-between gap-3 px-3 py-2.5 text-left hover:bg-gray-50 dark:hover:bg-gray-700/40 transition-colors"
                            >
                                <div className="flex items-start gap-2 min-w-0">
                                    <span className={`mt-0.5 shrink-0 inline-flex items-center px-1.5 py-0.5 text-[10px] font-semibold rounded uppercase ${
                                        err.severity === 'warn'
                                            ? 'bg-amber-50 dark:bg-amber-500/10 text-amber-600 dark:text-amber-400 border border-amber-100 dark:border-amber-500/20'
                                            : 'bg-red-50 dark:bg-red-500/10 text-red-600 dark:text-red-400 border border-red-100 dark:border-red-500/20'
                                    }`}>
                                        {err.severity}
                                    </span>
                                    <span className="text-xs text-gray-700 dark:text-gray-300 font-mono truncate">{err.message}</span>
                                </div>
                                <div className="flex items-center gap-2 shrink-0">
                                    <span className="text-[11px] text-gray-400">{timeAgo(err.createdAt)}</span>
                                    <HugeiconsIcon
                                        icon={ArrowRight01Icon}
                                        size={12}
                                        className={`text-gray-400 transition-transform ${expanded === i ? 'rotate-90' : ''}`}
                                    />
                                </div>
                            </button>
                            {expanded === i && err.stack && (
                                <div className="px-3 py-2.5 bg-gray-50 dark:bg-gray-900/50 border-t border-gray-100 dark:border-gray-700">
                                    <pre className="text-[11px] text-gray-500 dark:text-gray-400 font-mono whitespace-pre-wrap break-all leading-relaxed">
                                        {err.stack}
                                    </pre>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

function ServiceCard({ name, serviceKey, history, currentStatus, errors, errorsLoading }) {
    const dates = buildDateRange(DAYS);
    const byDate = {};
    (history || []).forEach(h => { byDate[h.date] = h.upPct; });

    const totalChecks  = (history || []).reduce((s, h) => s + h.total, 0);
    const totalUp      = (history || []).reduce((s, h) => s + Math.round(h.upPct / 100 * h.total), 0);
    const overallUpPct = totalChecks > 0 ? ((totalUp / totalChecks) * 100).toFixed(2) : null;

    const isUp      = currentStatus === 'up';
    const isPending = currentStatus === undefined;
    const errorCount = errors?.length ?? 0;

    return (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm p-5">
            <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2.5">
                    <span className="text-sm font-semibold text-gray-800 dark:text-gray-100">{name}</span>
                    {errorCount > 0 && (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-semibold bg-red-50 text-red-600 border border-red-100 rounded-full">
                            <HugeiconsIcon icon={Cancel01Icon} size={9} />
                            {errorCount}
                        </span>
                    )}
                </div>
                <div className="relative group">
                    <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full cursor-default ${
                        isPending ? 'bg-gray-100 dark:bg-gray-700 text-gray-500' :
                        isUp      ? 'bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border border-emerald-100 dark:border-emerald-500/20' :
                                    'bg-red-50 dark:bg-red-500/10 text-red-600 dark:text-red-400 border border-red-100 dark:border-red-500/20'
                    }`}>
                        {!isPending && (
                            <HugeiconsIcon icon={isUp ? CheckmarkCircle01Icon : Cancel01Icon} size={11} />
                        )}
                        {isPending ? 'Checking…' : isUp ? 'Operational' : 'Offline'}
                    </span>
                    {!isPending && (
                        <div className="absolute right-0 top-full mt-2 z-50 w-52 bg-gray-900 dark:bg-gray-700 text-white rounded-xl shadow-xl p-3.5 opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity duration-150">
                            <p className="text-xs font-semibold mb-2 text-gray-200">{name}</p>
                            <div className="space-y-1.5">
                                <div className="flex items-center justify-between text-[11px]">
                                    <span className="text-gray-400">Status</span>
                                    <span className={isUp ? 'text-emerald-400 font-medium' : 'text-red-400 font-medium'}>
                                        {isUp ? '● Online' : '● Offline'}
                                    </span>
                                </div>
                                {overallUpPct !== null && (
                                    <div className="flex items-center justify-between text-[11px]">
                                        <span className="text-gray-400">90-day uptime</span>
                                        <span className="text-gray-100 font-medium tabular-nums">{overallUpPct}%</span>
                                    </div>
                                )}
                                {errorCount > 0 && (
                                    <div className="flex items-center justify-between text-[11px]">
                                        <span className="text-gray-400">Errors (30d)</span>
                                        <span className="text-red-400 font-medium">{errorCount}</span>
                                    </div>
                                )}
                            </div>
                            <div className="absolute -top-1.5 right-4 w-3 h-3 bg-gray-900 dark:bg-gray-700 rotate-45 rounded-sm" />
                        </div>
                    )}
                </div>
            </div>

            <div className="flex items-end gap-px h-8">
                {dates.map(date => {
                    const upPct = byDate[date] !== undefined ? byDate[date] : null;
                    return (
                        <div
                            key={date}
                            className={`flex-1 rounded-sm ${barColor(upPct)} transition-all`}
                            style={{ minHeight: '100%' }}
                            title={upPct !== null ? `${date}: ${upPct}% uptime` : `${date}: no data`}
                        />
                    );
                })}
            </div>

            <div className="flex items-center justify-between mt-2 text-xs text-gray-400 dark:text-gray-500">
                <span>{DAYS} days ago</span>
                {overallUpPct !== null && (
                    <span className="text-gray-600 dark:text-gray-300 font-medium">{overallUpPct}% uptime</span>
                )}
                <span>Today</span>
            </div>

            <ErrorPanel errors={errors} loading={errorsLoading} />
        </div>
    );
}

function Index() {

    const accessToken = useSelector(selectAccessToken);
    const [currentStatus, setCurrentStatus] = useState({});
    const [history, setHistory]             = useState({});
    const [errors, setErrors]               = useState({});
    const [errorsLoading, setErrorsLoading] = useState(true);
    const [lastRefresh, setLastRefresh]     = useState(null);
    const [refreshing, setRefreshing]       = useState(false);

    const loadHistory = useCallback(async () => {
        const headers = authStaff(accessToken);
        const res = await fetchRequests(`/loans/staff/health-history?days=${DAYS}`, "GET", headers, {}, {});
        if (!res?.err) setHistory(res || {});
    }, [accessToken]);

    const loadErrors = useCallback(async () => {
        setErrorsLoading(true);
        const headers = authStaff(accessToken);
        const res = await fetchRequests(`/loans/staff/error-logs`, "GET", headers, {}, {});
        if (!res?.err && res?.logs) {
            // group by service
            const grouped = {};
            res.logs.forEach(log => {
                if (!grouped[log.service]) grouped[log.service] = [];
                grouped[log.service].push(log);
            });
            setErrors(grouped);
        }
        setErrorsLoading(false);
    }, [accessToken]);

    const checkAll = useCallback(async () => {
        setRefreshing(true);
        const headers = authStaff(accessToken);

        const results = await Promise.allSettled(
            SERVICES.map(async (s) => {
                const start = Date.now();
                const res = await fetchRequests(s.path, "GET", headers, {}, {});
                const responseMs = Date.now() - start;
                const status = res?.status === 'ok' ? 'up' : 'down';
                fetchRequests(`/loans/staff/health-log`, "POST", headers, {}, {
                    service: s.key, status, responseMs
                });
                return { key: s.key, status };
            })
        );

        const next = {};
        results.forEach(r => {
            if (r.status === 'fulfilled') next[r.value.key] = r.value.status;
        });

        setCurrentStatus(next);
        setLastRefresh(new Date());
        setRefreshing(false);
        loadHistory();
    }, [accessToken, loadHistory]);

    useEffect(() => {
        document.title = `Service Status - ${process.env.REACT_APP_SITE_NAME}`;
        loadHistory();
        loadErrors();
        checkAll();
        const interval = setInterval(checkAll, 30000);
        return () => clearInterval(interval);
    }, [checkAll, loadHistory, loadErrors]);

    const onlineCount = Object.values(currentStatus).filter(s => s === 'up').length;

    return (
        <div className="main-container">
            <Topbar />
            <Sidebar />

            <main className="main-content">
                <section className="section-container space-y-6">

                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className="w-9 h-9 rounded-xl bg-blue-50 border border-blue-100 flex items-center justify-center shrink-0">
                                <HugeiconsIcon icon={CheckmarkCircle01Icon} size={18} className="text-[#1e3a6e]" />
                            </div>
                            <div>
                                <h1 className="text-lg font-semibold text-gray-900 dark:text-gray-50">Service Status</h1>
                                <p className="text-sm text-gray-400">
                                    {Object.keys(currentStatus).length > 0
                                        ? `${onlineCount} of ${SERVICES.length} services operational`
                                        : `Uptime over the past ${DAYS} days`}
                                    {lastRefresh && ` · Last checked ${lastRefresh.toLocaleTimeString()}`}
                                </p>
                            </div>
                        </div>

                        <button
                            type="button"
                            onClick={checkAll}
                            disabled={refreshing}
                            className="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-white bg-[#1e3a6e] rounded-lg hover:bg-[#264d91] disabled:opacity-50 transition-colors"
                        >
                            {refreshing ? <Loader color="text-white" size="w-4 h-4" /> : <HugeiconsIcon icon={ArrowRight01Icon} size={15} />}
                            {refreshing ? 'Refreshing…' : 'Refresh'}
                        </button>
                    </div>

                    <div className="space-y-4">
                        {SERVICES.map(s => (
                            <ServiceCard
                                key={s.key}
                                name={s.name}
                                serviceKey={s.key}
                                history={history[s.key]}
                                currentStatus={currentStatus[s.key]}
                                errors={errors[s.key]}
                                errorsLoading={errorsLoading}
                            />
                        ))}
                    </div>

                    <div className="flex items-center gap-4 text-xs text-gray-400 dark:text-gray-500">
                        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-emerald-500 inline-block"/>≥ 95% uptime</span>
                        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-amber-400 inline-block"/>70–94%</span>
                        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-red-500 inline-block"/>{'< 70%'}</span>
                        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-gray-200 dark:bg-gray-700 inline-block"/>No data</span>
                    </div>

                </section>
            </main>
        </div>
    );
}

export default Index;
